/**
 * 拆帳設定 /commission-rules（20260611 反饋批次）
 * 4 服務類型固定列 × percent/fixed × 值；無規則時預設抽成 13%。
 */
import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { SERVICE_TYPE_META } from "../../../../shared/const";
import { CREATABLE_SERVICE_TYPES, type CreatableServiceType } from "../../../../shared/booking";
import { THEAD_ROW } from "./base-data-shared";

type Mode = "percent" | "fixed";
type Row = { serviceType: CreatableServiceType; mode: Mode; value: string };

export default function CommissionRules() {
  const { t } = useLanguage();
  const listQ = trpc.commissionRule.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];
  const byType = new Map(rows.map((r) => [r.serviceType, r]));

  const modeLoc = (mode: Mode) =>
    mode === "percent"
      ? { "zh-TW": "百分比", "en-US": "Percent", "zh-CN": "百分比" }
      : { "zh-TW": "固定金額", "en-US": "Fixed", "zh-CN": "固定金额" };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "拆帳設定", "en-US": "Commission Rules", "zh-CN": "拆帐设定" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "依服務類型設定平台抽成：百分比或固定金額（HKD）。未設定規則的類型，預設抽成 13%。",
            "en-US": "Platform commission per service type: percent or fixed HKD. Types without a rule default to 13%.",
            "zh-CN": "依服务类型设定平台抽成：百分比或固定金额（HKD）。未设定规则的类型，预设抽成 13%。",
          })}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "服務類型", "en-US": "Service", "zh-CN": "服务类型" })}</th>
                  <th className="p-3">{t({ "zh-TW": "模式", "en-US": "Mode", "zh-CN": "模式" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "抽成", "en-US": "Commission", "zh-CN": "抽成" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {CREATABLE_SERVICE_TYPES.map((st) => {
                  const r = byType.get(st);
                  return (
                    <tr key={st} className="border-b border-border/60 last:border-0">
                      <td className="p-3 font-medium">{t(SERVICE_TYPE_META[st].label)}</td>
                      <td className="p-3">
                        {r ? t(modeLoc(r.mode)) : (
                          <span className="text-muted-foreground/60">
                            {t({ "zh-TW": "未設定（預設 13%）", "en-US": "unset (default 13%)", "zh-CN": "未设定（预设 13%）" })}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right numeric font-medium">
                        {r ? (r.mode === "percent" ? `${Number(r.value)}%` : `HKD ${Number(r.value).toLocaleString()}`) : "13%"}
                      </td>
                      <td className="p-3 text-right">
                        <RuleDialog serviceType={st} existing={r} onDone={() => listQ.refetch()} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RuleDialog({
  serviceType,
  existing,
  onDone,
}: {
  serviceType: CreatableServiceType;
  existing?: Row;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(existing?.mode ?? "percent");
  const [value, setValue] = useState(existing ? Number(existing.value) : 13);

  const m = trpc.commissionRule.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  const invalid = !Number.isFinite(value) || value < 0 || (mode === "percent" && value > 100);

  // 開啟時從 props/初始值重新同步草稿，避免殘留上一次取消的輸入
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setMode(existing?.mode ?? "percent");
      setValue(existing ? Number(existing.value) : 13);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {existing ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "設定", "en-US": "Set", "zh-CN": "设定" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(SERVICE_TYPE_META[serviceType].label)}</DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "百分比＝售價的 %；固定金額＝每單固定 HKD。未設定時預設 13%。",
              "en-US": "Percent = % of price; fixed = flat HKD per order. Defaults to 13% when unset.",
              "zh-CN": "百分比＝售价的 %；固定金额＝每单固定 HKD。未设定时预设 13%。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "模式", "en-US": "Mode", "zh-CN": "模式" })}</Label>
              <Select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
                <option value="percent">{t({ "zh-TW": "百分比", "en-US": "Percent", "zh-CN": "百分比" })}</option>
                <option value="fixed">{t({ "zh-TW": "固定金額", "en-US": "Fixed", "zh-CN": "固定金额" })}</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>
                {t({ "zh-TW": "值", "en-US": "Value", "zh-CN": "值" })}（{mode === "percent" ? "%" : "HKD"}）
              </Label>
              <Input
                type="number"
                min={0}
                max={mode === "percent" ? 100 : undefined}
                step="0.01"
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
              />
            </div>
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={() => m.mutate({ serviceType, mode, value })} disabled={m.isPending || invalid}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
